/**
 * NavStack context + hooks (navigation overhaul, chunk A1).
 *
 * The provider component lives in src/components/NavStackProvider.tsx (split
 * out so this file exports only non-components — react-refresh/only-export-
 * components, the rule #26 separation). This file owns the context + the two
 * consumer hooks:
 *   - useNavStack(): the coordinator (for resetToBase / requestClose helpers).
 *   - useNavLayer(key, isOpen, onRequestClose): register a dismissable surface
 *     as a history layer; returns the computed z-index (undefined when closed)
 *     via a useSyncExternalStore subscription (§3.2b).
 *
 * Registration MUST be called from a stable owner that mounts CLOSED (§3.2a):
 * push on the [isOpen] closed->open edge; teardown does "still registered?
 * requestClose : no-op" (the B5 protocol that makes setOpen(false) and
 * requestClose converge).
 */
import { createContext, useContext, useLayoutEffect, useRef, useSyncExternalStore } from 'react';
import { NAV_Z_BASE, NAV_Z_STEP, type NavStack } from '@/lib/nav-stack';

export const NavStackContext = createContext<NavStack | null>(null);

export function useNavStack(): NavStack {
  const c = useContext(NavStackContext);
  if (c === null) throw new Error('useNavStack must be used within NavStackProvider');
  return c;
}

export function useNavLayer(
  key: string,
  isOpen: boolean,
  onRequestClose: () => void,
): number | undefined {
  const coord = useNavStack();

  // Latest callback held in a ref so re-renders / identity churn never
  // re-register; the stored wrapper always reads the current callback.
  const closeRef = useRef(onRequestClose);
  closeRef.current = onRequestClose;

  // Registration on [isOpen] ONLY (key + coord are stable). useLayoutEffect so
  // the history entry exists before the overlay paints.
  useLayoutEffect(() => {
    if (!isOpen) return;
    coord.push(key, () => closeRef.current());
    return () => {
      // Teardown (open->close or unmount): if the reconciler hasn't already
      // removed this layer, move history to match; otherwise no-op (B5).
      if (coord.has(key)) coord.requestClose(key);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- register on isOpen edge only
  }, [isOpen]);

  const index = useSyncExternalStore(coord.subscribe, () => coord.indexSnapshot(key));
  return index < 0 ? undefined : NAV_Z_BASE + index * NAV_Z_STEP;
}
